// SEP-2 / E0 — ManageEntity: the generalized owner CRUD shell. One component owns the full read + write
// lifecycle for ANY owner-managed collection over the ownsApp-gated generic owner route:
//   • LIST    GET    /v1/apps/:appId/owner/:collection            (rows are exactly the server's — no fabrication)
//   • CREATE  POST   /v1/apps/:appId/owner/:collection { data }   (guided form; server-set lifecycle status)
//   • EDIT    PATCH  /v1/apps/:appId/owner/:collection/:id { data }   (shallow field merge)
//   • STATUS  PATCH  /v1/apps/:appId/owner/:collection/:id { status } (lifecycle-validated transition)
//   • ARCHIVE PATCH  …/:id { data:{ archived:true } }             (reversible — the safe default)
//   • DELETE  DELETE …/:id                                        (guarded + confirmed by the caller)
//   • CSV     GET    …/:collection/export.csv
// It renders the honest four states via ConsoleSection, mounts the guided EntityFormSheet for add/edit,
// and hands each row to a caller-provided renderItem render-prop with an actions context — so it stays
// category-agnostic (Shop products/items/contacts/deals AND Booking services all reuse it) while each
// console owns its own row presentation + which primitives (StateTransitionBar / DestructiveActionGuard)
// it composes. No optimistic lies: a row only changes after the server 2xx.
import { useCallback, useEffect, useMemo, useState } from 'preact/hooks';
import type { JSX } from 'preact';
import { Plus, Download } from 'lucide-preact';
import { t } from '../lib/i18n';
import { ConsoleSection } from './ConsoleSection';
import type { ConsoleState } from './ConsoleSection';
import { EntityFormSheet } from './EntityFormSheet';
import type { EntityField, FieldValue, FormSubmitResult } from './EntityFormSheet';

/** A row of an owner-managed collection: `id` + the flattened data fields the owner route returns. */
export interface EntityRow {
  id: string;
  archived?: boolean;
  [k: string]: unknown;
}

/** The action context handed to each row's renderItem. Every write hits the real owner route. */
export interface EntityActions<R extends EntityRow = EntityRow> {
  /** open the guided form prefilled from this row (edit). */
  edit: (row: R) => void;
  /** shallow-merge data fields — PATCH { data }. Resolves true on 2xx. */
  patch: (id: string, data: Record<string, unknown>) => Promise<boolean>;
  /** lifecycle transition — PATCH { status } (server-validated). Resolves true on 2xx. */
  setStatus: (id: string, status: string) => Promise<boolean>;
  /** archive (reversible) — PATCH { data:{ archived:true } }. */
  archive: (row: R) => Promise<boolean>;
  /** restore — PATCH { data:{ archived:false } }. */
  restore: (row: R) => Promise<boolean>;
  /** hard delete — DELETE …/:id. */
  remove: (row: R) => Promise<boolean>;
  /** the row currently mutating (drives per-row spinners / disabled state). */
  busyId: string | null;
}

export interface ManageEntityProps<R extends EntityRow = EntityRow> {
  appId: string;
  /** the owner-managed collection (base or namespaced, e.g. 'products' or 'crm-contacts'). */
  collection: string;
  titleKey: string;
  icon?: JSX.Element;
  emptyTitleKey: string;
  emptyBodyKey: string;
  emptyIcon?: JSX.Element;
  /** guided-form schema. When present + creatable, an Add button + the form appear. */
  fields?: readonly EntityField[];
  creatable?: boolean;
  addLabelKey?: string;
  formTitleAddKey?: string;
  formTitleEditKey?: string;
  /** default values for a new (create) row. */
  newDefaults?: Record<string, FieldValue>;
  /** map a row → guided-form values for the edit prefill (default: the row itself, minus id). */
  toFormValues?: (row: R) => Record<string, FieldValue>;
  /** map submitted form values → the data payload sent to the server (default: identity). */
  toPayload?: (values: Record<string, FieldValue>) => Record<string, unknown>;
  csvExport?: boolean;
  /** render one row + its actions. */
  renderItem: (row: R, actions: EntityActions<R>) => JSX.Element;
  /** classify a row as archived (default: !!row.archived). */
  isArchived?: (row: R) => boolean;
  /** show the Show/Hide-archived filter (default true). */
  archiveFilter?: boolean;
  unavailableTitleKey?: string;
  unavailableBodyKey?: string;
  errorAuthKey?: string;
}

export function ManageEntity<R extends EntityRow = EntityRow>({
  appId, collection, titleKey, icon, emptyTitleKey, emptyBodyKey, emptyIcon,
  fields, creatable, addLabelKey = 'own.add', formTitleAddKey = 'own.addTitle', formTitleEditKey = 'own.editTitle',
  newDefaults, toFormValues, toPayload, csvExport, renderItem,
  isArchived = (row) => !!row.archived, archiveFilter = true,
  unavailableTitleKey = 'own.unavailableT', unavailableBodyKey = 'own.unavailableS', errorAuthKey = 'own.errAuth',
}: ManageEntityProps<R>): JSX.Element {
  const [rows, setRows] = useState<R[]>([]);
  const [state, setState] = useState<ConsoleState>({ status: 'loading' });
  const [busyId, setBusyId] = useState<string | null>(null);
  const [showArchived, setShowArchived] = useState(false);
  const [editing, setEditing] = useState<R | null>(null);
  const [adding, setAdding] = useState(false);

  const base = `/v1/apps/${encodeURIComponent(appId)}/owner/${encodeURIComponent(collection)}`;

  const fetchRows = useCallback(() => {
    setState({ status: 'loading' });
    fetch(base, { credentials: 'include' })
      .then(async (r) => {
        if (r.status === 503) return setState({ status: 'unavailable' });
        if (!r.ok) return setState({ status: 'error', code: r.status });
        const data = (await r.json()) as { items?: R[] };
        setRows(Array.isArray(data.items) ? data.items : []);
        setState({ status: 'ready' });
      })
      .catch(() => setState({ status: 'error' }));
  }, [base]);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  const replaceRow = (item: R): void => setRows((cur) => cur.map((x) => (x.id === item.id ? item : x)));

  const patch = useCallback(async (id: string, data: Record<string, unknown>): Promise<boolean> => {
    setBusyId(id);
    try {
      const r = await fetch(`${base}/${encodeURIComponent(id)}`, {
        method: 'PATCH', credentials: 'include', headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ data }),
      });
      if (!r.ok) return false;
      const out = (await r.json().catch(() => ({}))) as { item?: R };
      if (out.item) replaceRow(out.item);
      return true;
    } catch { return false; } finally { setBusyId(null); }
  }, [base]);

  const setStatus = useCallback(async (id: string, status: string): Promise<boolean> => {
    setBusyId(id);
    try {
      const r = await fetch(`${base}/${encodeURIComponent(id)}`, {
        method: 'PATCH', credentials: 'include', headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ status }),
      });
      if (!r.ok) return false;
      const out = (await r.json().catch(() => ({}))) as { item?: R };
      if (out.item) replaceRow(out.item);
      return true;
    } catch { return false; } finally { setBusyId(null); }
  }, [base]);

  const remove = useCallback(async (row: R): Promise<boolean> => {
    setBusyId(row.id);
    try {
      const r = await fetch(`${base}/${encodeURIComponent(row.id)}`, { method: 'DELETE', credentials: 'include' });
      if (!r.ok) return false;
      setRows((cur) => cur.filter((x) => x.id !== row.id));
      return true;
    } catch { return false; } finally { setBusyId(null); }
  }, [base]);

  const archive = useCallback((row: R) => patch(row.id, { archived: true }), [patch]);
  const restore = useCallback((row: R) => patch(row.id, { archived: false }), [patch]);

  const actions: EntityActions<R> = useMemo(() => ({
    edit: (row) => setEditing(row),
    patch, setStatus, archive, restore, remove, busyId,
  }), [patch, setStatus, archive, restore, remove, busyId]);

  async function submitForm(values: Record<string, FieldValue>): Promise<FormSubmitResult> {
    const data = toPayload ? toPayload(values) : (values as Record<string, unknown>);
    if (editing) {
      const ok = await patch(editing.id, data);
      return ok ? { ok: true } : { ok: false, errorKey: 'own.saveFailed' };
    }
    // create
    try {
      const r = await fetch(base, {
        method: 'POST', credentials: 'include', headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ data }),
      });
      if (r.status === 201 || r.ok) {
        const out = (await r.json().catch(() => ({}))) as { item?: R };
        if (out.item) setRows((cur) => [...cur, out.item as R]);
        return { ok: true };
      }
      if (r.status === 401) return { ok: false, errorKey: errorAuthKey };
      if (r.status === 400) return { ok: false, errorKey: 'own.invalidRecord' };
      return { ok: false, errorKey: 'own.saveFailed' };
    } catch {
      return { ok: false, errorKey: 'own.saveFailed' };
    }
  }

  const visible = rows.filter((r) => showArchived || !isArchived(r));
  const archivedCount = rows.filter(isArchived).length;
  const canAdd = creatable && fields && fields.length > 0;

  const editValues = editing
    ? (toFormValues ? toFormValues(editing) : stripId(editing))
    : (newDefaults ?? {});

  return (
    <>
      <ConsoleSection
        titleKey={titleKey}
        icon={icon}
        count={visible.length}
        state={state}
        isEmpty={state.status === 'ready' && visible.length === 0}
        onRefresh={fetchRows}
        onRetry={fetchRows}
        emptyTitleKey={emptyTitleKey}
        emptyBodyKey={emptyBodyKey}
        emptyIcon={emptyIcon}
        unavailableTitleKey={unavailableTitleKey}
        unavailableBodyKey={unavailableBodyKey}
        errorAuthKey={errorAuthKey}
        headerActions={
          <>
            {archiveFilter && archivedCount > 0 && (
              <button type="button" className="pf-btn pf-btn--sm pf-btn--ghost" aria-pressed={showArchived ? 'true' : 'false'} onClick={() => setShowArchived((s) => !s)}>
                {showArchived ? t('own.hideArchived') : t('own.showArchived', { n: archivedCount })}
              </button>
            )}
            {csvExport && (
              <a className="pf-btn pf-btn--sm" href={`${base}/export.csv`} download>
                <Download size={13} /> {t('mgmt.rec.exportCsv')}
              </a>
            )}
            {canAdd && (
              <button type="button" className="pf-btn pf-btn--sm pf-btn--soft" onClick={() => { setEditing(null); setAdding(true); }}>
                <Plus size={13} /> {t(addLabelKey)}
              </button>
            )}
          </>
        }
      >
        <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
          {visible.map((row) => (
            <li key={row.id}>{renderItem(row, actions)}</li>
          ))}
        </ul>
      </ConsoleSection>

      {/* Empty-but-creatable: give the honest empty state an Add path too (the header CTA is hidden when empty). */}
      {canAdd && state.status === 'ready' && visible.length === 0 && (
        <div style={{ display: 'flex', justifyContent: 'center', marginTop: -4 }}>
          <button type="button" className="pf-btn pf-btn--sm pf-btn--soft" onClick={() => { setEditing(null); setAdding(true); }}>
            <Plus size={13} /> {t(addLabelKey)}
          </button>
        </div>
      )}

      {fields && fields.length > 0 && (
        <EntityFormSheet
          open={adding || !!editing}
          titleKey={editing ? formTitleEditKey : formTitleAddKey}
          fields={fields}
          appId={appId}
          initialValues={editValues}
          onSubmit={submitForm}
          onClose={() => { setAdding(false); setEditing(null); }}
        />
      )}
    </>
  );
}

function stripId<R extends EntityRow>(row: R): Record<string, FieldValue> {
  const { id, ...rest } = row;
  void id;
  return rest as Record<string, FieldValue>;
}
